/**
 * Firestore OTP Service
 * Implements the same OTP logic as the backend using Firestore
 * Stores and verifies OTPs directly in Firestore collections
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase-config';
import { getFirebaseProjectId } from '../utils/firebaseProject';
import sessionManager from './sessionManager';

const isEmulator = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true';
const projectId = getFirebaseProjectId();

const getFunctionUrl = (functionName) => {
  if (isEmulator) {
    // Functions emulator runs on port 5002 as per firebase.json
    return `http://127.0.0.1:5002/${projectId}/asia-south1/${functionName}`;
  }
  return `https://asia-south1-${projectId}.cloudfunctions.net/${functionName}`;
};

class FirestoreOTPService {
  constructor() {
    this.db = db;
    this.otpExpiryMinutes = 10;
    this.maxAttempts = 3;
  }

  /**
   * Convert email to document ID format
   */
  emailToDocId(email) {
    if (!email) return null;
    return email.replace(/[.@]/g, (match) => {
      if (match === '.') return '_';
      if (match === '@') return '_at_';
      return match;
    });
  }

  /**
   * Request an OTP from the backend Firebase Function.
   */
  async requestOTP(email) {
    try {
      // First, check if the user exists in the 'users' collection by querying the email field.
      const usersRef = collection(this.db, 'users');
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.error(`OTP request denied: User '${email}' not found via email query.`);
        return { success: false, message: 'Access denied. Contact Admin or support.' };
      }

      console.log('🔄 Requesting OTP from backend for:', email);
      const url = getFunctionUrl('directOtpRequest');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        console.log('✅ OTP requested successfully from backend:', result.message);
        return { success: true, message: result.message };
      } else {
        console.error('❌ Failed to request OTP from backend:', result.message);
        return { success: false, message: result.message || 'Failed to request OTP from the server.' };
      }
    } catch (error) {
      console.error('Error in requestOTP:', error);
      // Check if it's a JSON parsing error
      if (error instanceof SyntaxError) {
          return { success: false, message: 'Received an invalid response from the server.' };
      }
      return { success: false, message: `A network error occurred. Please try again.` };
    }
  }

  /**
   * Verify the provided OTP by calling the backend.
   */
  async verifyOTP(email, providedOTP) {
    try {
      console.log('🔄 Verifying OTP via backend for:', email);
      const url = getFunctionUrl('verifyOTP');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp: providedOTP }),
      });

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        console.log('✅ OTP verified successfully via backend');
        return { success: true, message: result.message };
      } else {
        console.error('❌ OTP verification failed via backend:', result.message);
        return { success: false, message: result.message || 'Invalid OTP or server error.' };
      }
    } catch (error) {
      console.error('Error in verifyOTP API call:', error);
      if (error instanceof SyntaxError) {
        return { success: false, message: 'Received an invalid response from the server during verification.' };
      }
      return { success: false, message: 'A network error occurred during verification.' };
    }
  }

  /**
   * Verify OTP, fetch/create user, create session, and return all data
   * @param {string} email - User's email
   * @param {string} otp - OTP code from user
   * @returns {Promise<Object>} Comprehensive login result object
   */
  async verifyOtpAndLogin(email, otp) {
    try {
      // Step 1: Verify OTP
      const otpResult = await this.verifyOTP(email, otp);
      if (!otpResult.success) {
        return { success: false, message: otpResult.message };
      }

      // Step 2: Fetch or create user profile in Firestore
      const usersRef = collection(this.db, 'users');
      const userDocId = this.emailToDocId(email);
      const userDocRef = doc(usersRef, userDocId);
      let userSnap = await getDoc(userDocRef);

      let userData;
      if (userSnap.exists()) {
        // User exists, update last login
        console.log('User found, updating last login');
        await updateDoc(userDocRef, { last_login: serverTimestamp() });
        userData = { uid: userDocRef.id, ...userSnap.data() };
      } else {
        // User does not exist, so deny access.
        console.error(`Login attempt failed: User with email ${email} not found in 'users' collection.`);
        return { success: false, message: 'Access denied. Contact Admin or support.' };
      }

      // Step 3: Create a client-side session
      const sessionResult = await sessionManager.createSession(email, userData);
      if (!sessionResult.success) {
        return { success: false, message: 'Failed to create session.' };
      }

      // Step 4: Assemble the final payload
      // Permissions would ideally come from the user's roles
      const permissions = { canViewDashboard: true }; // Placeholder
      const role = userData.roles.includes('admin') ? 'admin' : 'user';

      return {
        success: true,
        user: userData,
        session: {
          id: sessionResult.sessionId,
          expiresAt: sessionResult.expiresAt,
        },
        permissions,
        organization: userData.organization,
        role,
      };
    } catch (error) {
      console.error('Error in verifyOtpAndLogin:', error);
      return { success: false, message: `Login error: ${error.message}` };
    }
  }

  /**
   * Clean up expired OTPs (can be called periodically)
   * @returns {Promise<{success: boolean, cleanedCount?: number, error?: string}>}
   */
  async cleanupExpiredOTPs() {
    try {
      const cutoffTime = new Date(Date.now() - (60 * 60 * 1000)); // Clean up OTPs older than 1 hour

      const expiredOTPsQuery = query(
        collection(this.db, 'otps'),
        where('created_at', '<', Timestamp.fromDate(cutoffTime))
      );

      const expiredOTPs = await getDocs(expiredOTPsQuery);

      // Note: Firestore doesn't support batch deletes in client SDK
      // This would need to be done on the backend or with Cloud Functions
      console.log('Found', expiredOTPs.size, 'expired OTP records to clean up');

      return { success: true, cleanedCount: expiredOTPs.size };
    } catch (error) {
      console.error('Error cleaning up expired OTPs:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get service status and configuration
   * @returns {Object} Service information
   */
  getServiceInfo() {
    return {
      serviceName: 'Firestore OTP Service',
      otpExpiryMinutes: this.otpExpiryMinutes,
      maxAttempts: this.maxAttempts,
      rateLimitMinutes: this.rateLimitMinutes, // This property is removed, so it will be undefined
      collections: {
        otpVerification: 'otps',
        otpRequests: 'otp_requests'
      },
      isConfigured: !!this.db
    };
  }
}

// Create singleton instance
const firestoreOTPService = new FirestoreOTPService();

export default firestoreOTPService;
